/**
 * Shared constants for the onboarding service
 *
 * IMPORTANT: This is the single source of truth for all configuration constants.
 * Do not duplicate these values elsewhere in the codebase.
 */

// =============================================================================
// TOKEN CONFIGURATION
// =============================================================================

export const TOKEN_CONFIG = {
  /** Token expiration time in seconds (10 minutes) */
  EXPIRATION_SECONDS: 10 * 60,
  /** Warning threshold in milliseconds (1 minute - shows seconds instead of minutes) */
  WARNING_THRESHOLD_MS: 1 * 60 * 1000,
  /** Token expiration in milliseconds */
  EXPIRATION_MS: 10 * 60 * 1000,
  /** Maximum token age in seconds (24 hours) - tokens older than this are rejected */
  MAX_AGE_SECONDS: 24 * 60 * 60,
  /** Minimum JWT secret length for security (64 chars = 512 bits for HS256) */
  MIN_JWT_SECRET_LENGTH: 64,
} as const;

// =============================================================================
// FILE UPLOAD CONFIGURATION
// =============================================================================

export const FILE_UPLOAD = {
  /** Maximum file size in bytes (2MB) */
  MAX_SIZE_BYTES: 2 * 1024 * 1024,
  /** Maximum file size in MB (for display) */
  MAX_SIZE_MB: 2,
  /** Allowed MIME types */
  ALLOWED_TYPES: ["application/pdf"] as const,
  /** Pre-signed URL expiry in seconds (5 minutes) */
  PRESIGNED_URL_EXPIRY_SECONDS: 300,
  /** Maximum file name length after sanitization */
  MAX_FILENAME_LENGTH: 100,
  // carpeta raíz de las cédulas en DOCUMENTS_BUCKET: cedulas/{enterpriseId}/{fichero}
  KEY_PREFIX: "cedulas",
} as const;

// =============================================================================
// REQUEST LIMITS
// =============================================================================

export const REQUEST_LIMITS = {
  /** Maximum request body size in bytes (1MB) */
  MAX_BODY_SIZE_BYTES: 1024 * 1024,
} as const;

// =============================================================================
// VALIDATION PATTERNS
// =============================================================================

/**
 * RFC (Registro Federal de Contribuyentes) validation pattern
 * Format: 3-4 letters + 6 digits (YYMMDD) + 3 alphanumeric (homoclave)
 * Examples: XAXX010101ABC (persona moral), XEXX010101AB1 (persona física)
 */
export const RFC_PATTERN = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;

/**
 * SAT Password validation pattern
 * Requirements: min 12 chars, uppercase, lowercase, number, special char
 */
export const SAT_PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{12,}$/;

// =============================================================================
// ERROR CODES
// =============================================================================

/**
 * Standardized error codes returned by the API.
 *
 * Usage:
 * - INVALID_TOKEN: JWT signature verification failed or malformed token
 * - EXPIRED_TOKEN: Token has exceeded its expiration time
 * - USED_TOKEN: One-time use token has already been consumed
 * - MISSING_TOKEN: No token provided in the request
 * - INVALID_RFC: RFC format validation failed
 * - RFC_MISMATCH: Submitted RFC doesn't match the token's RFC
 * - INVALID_RAZON_SOCIAL: Business name validation failed
 * - SESSION_NOT_FOUND: Session ID doesn't exist in database
 * - SESSION_EXPIRED: Session has exceeded its TTL
 * - UNAUTHORIZED: Authentication required but not provided/invalid
 * - INTERNAL_ERROR: Unexpected server error
 * - VALIDATION_ERROR: Generic input validation failure
 * - NOT_FOUND: Resource doesn't exist (or isn't the caller's: same answer)
 * - NOT_REACTIVATABLE: SAT connection can't be reactivated by an admin
 */
export const ERROR_CODES = {
  INVALID_TOKEN: "INVALID_TOKEN",
  EXPIRED_TOKEN: "EXPIRED_TOKEN",
  USED_TOKEN: "USED_TOKEN",
  MISSING_TOKEN: "MISSING_TOKEN",
  INVALID_RFC: "INVALID_RFC",
  RFC_MISMATCH: "RFC_MISMATCH",
  INVALID_RAZON_SOCIAL: "INVALID_RAZON_SOCIAL",
  SESSION_NOT_FOUND: "SESSION_NOT_FOUND",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  UNAUTHORIZED: "UNAUTHORIZED",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  NOT_FOUND: "NOT_FOUND",
  // la conexión SAT no está en un estado que un admin pueda reactivar (sat-connection, C1)
  NOT_REACTIVATABLE: "NOT_REACTIVATABLE",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// =============================================================================
// SESSION STATES
// =============================================================================

/**
 * Valid session state transitions:
 * - pending -> in_progress (when user validates token)
 * - in_progress -> completed (when user submits form)
 * - Any state can become expired (via TTL)
 */
export const SESSION_STATES = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  EXPIRED: "expired",
} as const;

export type SessionState = (typeof SESSION_STATES)[keyof typeof SESSION_STATES];

// =============================================================================
// RÉGIMEN FISCAL (Mexican Tax Regimes)
// =============================================================================

/**
 * SAT Régimen Fiscal codes
 * Reference: Catálogo de Régimen Fiscal del SAT
 * @see https://www.sat.gob.mx/consulta/23972/conoce-el-catalogo-del-cfdi
 */
export const REGIMEN_FISCAL = {
  "601": "General de Ley Personas Morales",
  "603": "Personas Morales con Fines no Lucrativos",
  "605": "Sueldos y Salarios e Ingresos Asimilados a Salarios",
  "606": "Arrendamiento",
  "607": "Régimen de Enajenación o Adquisición de Bienes",
  "608": "Demás ingresos",
  "610": "Residentes en el Extranjero sin Establecimiento Permanente en México",
  "611": "Ingresos por Dividendos (socios y accionistas)",
  "612": "Personas Físicas con Actividades Empresariales y Profesionales",
  "614": "Ingresos por intereses",
  "615": "Régimen de los ingresos por obtención de premios",
  "616": "Sin obligaciones fiscales",
  "620": "Sociedades Cooperativas de Producción que optan por diferir sus ingresos",
  "621": "Incorporación Fiscal",
  "622": "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
  "623": "Opcional para Grupos de Sociedades",
  "624": "Coordinados",
  "625": "Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas",
  "626": "Régimen Simplificado de Confianza",
} as const;

export type RegimenFiscalCode = keyof typeof REGIMEN_FISCAL;

// =============================================================================
// ENVIRONMENT VARIABLE NAMES
// =============================================================================

/**
 * Environment variable names used by the application.
 * Use these constants to avoid typos in env var names.
 */
export const ENV_VARS = {
  JWT_SECRET: "JWT_SECRET",
  ADMIN_API_KEY: "ADMIN_API_KEY",
  KMS_KEY_ID: "KMS_KEY_ID",
  SESSIONS_TABLE: "SESSIONS_TABLE",
  CONNECTIONS_TABLE: "CONNECTIONS_TABLE",
  DOCUMENTS_BUCKET: "DOCUMENTS_BUCKET",
  ALLOWED_ORIGINS: "ALLOWED_ORIGINS",
  NODE_ENV: "NODE_ENV",
  ONBOARDING_BASE_URL: "ONBOARDING_BASE_URL",
  // región de las tablas compartidas de george (george_prod_onboardings, george_prod_companies)
  GEORGE_TABLES_REGION: "GEORGE_TABLES_REGION",
  // bucket archives del mercado, donde submit deja la copia de la cédula para la recuperación
  GEORGE_ARCHIVES_BUCKET: "GEORGE_ARCHIVES_BUCKET",
  // topic george-sat-requests (mx-central-1) al que se publica sat-verify; vacío = no se publica
  SAT_REQUESTS_TOPIC_ARN: "SAT_REQUESTS_TOPIC_ARN",
} as const;

// =============================================================================
// VALORES POR DEFECTO DE MX
// =============================================================================

/**
 * valores de george en MX: el único mercado con valor por defecto en las Lambdas (MX y ejecuciones
 * locales sin región). el resto de mercados los recibe de sst.config (MARKETS) y no cae nunca a MX
 */
export const MX_GEORGE_DEFAULTS = {
  TABLES_REGION: "mx-central-1",
  ARCHIVES_BUCKET: "george-archives-mx",
} as const;
